import React from 'react';
import { Head, Link, useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save, Code, AlertCircle } from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

interface Document { id: number; name: string; }
interface ExtractJob { id: number; job_id: string; name?: string; }
interface Divisor {
  id?: number;
  divisor_type?: string;
  title?: string;
  content_preview?: string;
  parent_id?: number | null;
  metadata?: any;
}
interface ParentDivisor { id: number; title: string; }
interface DivisorFormProps {
  document: Document;
  extractJob: ExtractJob;
  divisor: Divisor;
  parentDivisors: ParentDivisor[];
  isEdit: boolean;
  errors: Record<string, string[]>;
  divisorTypes: string[];
}

export default function DivisorForm({
  document,
  extractJob,
  divisor,
  parentDivisors,
  isEdit,
  errors = {},
  divisorTypes
}: DivisorFormProps) {
  const {
    data,
    setData,
    post,
    put,
    processing,
    errors: formErrors,
    setError,
    clearErrors,
    transform,
  } = useForm({
    // What we will actually submit
    divisor: {
      divisor_type: divisor.divisor_type ?? '',
      title: divisor.title ?? '',
      content_preview: divisor.content_preview ?? '',
      // keep parent_id as string for the Select; normalize to null on submit
      parent_id: divisor.parent_id != null ? String(divisor.parent_id) : 'none',
      // will be replaced with parsed object on submit
      metadata: {} as Record<string, any>,
    },
    // Textarea source of truth for JSON
    metadata_json:
      typeof divisor.metadata === 'object' && divisor.metadata !== null
        ? JSON.stringify(divisor.metadata, null, 2)
        : (divisor.metadata ?? ''),
  });

  const allErrors = { ...errors, ...formErrors };

  const setDivisorField = <K extends keyof typeof data.divisor>(
    key: K,
    value: (typeof data.divisor)[K]
  ) => setData('divisor', { ...data.divisor, [key]: value });

  function parseJsonSafe(input: string): any | '__INVALID__' {
    if (!input || input.trim() === '') return {};
    try {
      const parsed = JSON.parse(input);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return '__INVALID__';
      return parsed;
    } catch {
      return '__INVALID__';
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors('metadata_json');

    const parsed = parseJsonSafe(data.metadata_json as string);
    if (parsed === '__INVALID__') {
      setError('metadata_json', 'Metadata must be valid JSON object (e.g., { "key": "value" }).');
      return;
    }

    const normalizedParent =
      data.divisor.parent_id === '' || data.divisor.parent_id === 'none'
        ? null
        : data.divisor.parent_id;

    // Build the exact payload synchronously so we don't depend on async setData
    const finalDivisor = {
      ...data.divisor,
      parent_id: normalizedParent as any,
      metadata: parsed
    };

    // Send ONLY { divisor: ... } to Rails
    transform(() => ({ divisor: finalDivisor }));

    const urlBase = `/documents/${document.id}/extract_jobs/${extractJob.id}/divisors`;
    if (isEdit && divisor.id) {
      put(`${urlBase}/${divisor.id}`);
    } else {
      post(urlBase);
    }
  };


  const generateSampleMetadata = () => {
    const sample = {
      title: data.divisor.title || '',
      content_preview: data.divisor.content_preview || '',
    };
    setData('metadata_json', JSON.stringify(sample, null, 2));
  };

  const hasAnyErrors = Object.keys(allErrors).some((k) => allErrors[k] && allErrors[k].length > 0);

  return (
    <>
      <Head title={`${isEdit ? 'Edit' : 'New'} Divisor - ${document.name}`} />

      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: 'Extract Jobs', href: `/documents/${document.id}/extract_jobs/${extractJob.id}/edit` },
              { label: 'Divisors', href: `/documents/${document.id}/extract_jobs/${extractJob.id}/divisors` },
              { label: isEdit ? 'Edit Divisor' : 'New Divisor', current: true }
            ]}
          />

          <div className="flex items-center gap-4">
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Divisors
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              {isEdit ? 'Edit' : 'New'} Divisor for "{document.name}"
            </h1>
          </div>

          {hasAnyErrors && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">Please fix the following errors:</div>
                <ul className="list-disc list-inside space-y-1">
                  {Object.entries(allErrors).map(([field, fieldErrors]) =>
                    fieldErrors && fieldErrors.map((error, index) => (
                      <li key={`${field}-${index}`}>{error}</li>
                    ))
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Divisor Type */}
                  <div className="space-y-2">
                    <Label htmlFor="divisor_type">Divisor Type *</Label>
                    <Select
                      value={data.divisor.divisor_type}
                      onValueChange={(value) => setDivisorField('divisor_type', value)}
                    >
                      <SelectTrigger id="divisor_type" className={allErrors.divisor_type ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select a divisor type" />
                      </SelectTrigger>
                      <SelectContent>
                        {divisorTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {allErrors.divisor_type && (
                      <div className="text-sm text-red-600">{allErrors.divisor_type.join(', ')}</div>
                    )}
                  </div>

                  {/* Parent */}
                  <div className="space-y-2">
                    <Label htmlFor="parent_id">Parent Divisor</Label>
                    <Select
                      value={String(data.divisor.parent_id)}
                      onValueChange={(value) => setDivisorField('parent_id', value)}
                    >
                      <SelectTrigger className={allErrors.parent_id ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select parent (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Parent - Top Level</SelectItem>
                        {parentDivisors
                          .filter((p) => p.id != null)
                          .map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              {p.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {allErrors.parent_id && (
                      <div className="text-sm text-red-600">{allErrors.parent_id.join(', ')}</div>
                    )}
                  </div>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={data.divisor.title}
                    onChange={(e) => setDivisorField('title', e.target.value)}
                    className={allErrors.title ? 'border-red-500' : ''}
                    required
                  />
                  {allErrors.title && (
                    <div className="text-sm text-red-600">{allErrors.title.join(', ')}</div>
                  )}
                </div>

                {/* Content Preview */}
                <div className="space-y-2">
                  <Label htmlFor="content_preview">Content Preview</Label>
                  <Textarea
                    id="content_preview"
                    value={data.divisor.content_preview}
                    onChange={(e) => setDivisorField('content_preview', e.target.value)}
                    className={`min-h-[120px] ${allErrors.content_preview ? 'border-red-500' : ''}`}
                    rows={5}
                  />
                  {allErrors.content_preview && (
                    <div className="text-sm text-red-600">{allErrors.content_preview.join(', ')}</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* JSON */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  JSON Metadata
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateSampleMetadata}
                    className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800"
                  >
                    Generate Sample JSON Metadata
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="metadata_json">Metadata JSON</Label>
                  <Textarea
                    id="metadata_json"
                    value={data.metadata_json as string}
                    onChange={(e) => setData('metadata_json', e.target.value)}
                    className={`min-h-[250px] font-mono text-sm ${allErrors.metadata ? 'border-red-500' : ''}`}
                    placeholder='e.g. { "title": "/243/Brushy-Lake-Park", "content_preview": "..." }'
                    rows={10}
                  />
                  <div className="text-xs text-gray-600">Enter a valid JSON object (not an array or scalar).</div>
                  {'metadata_json' in allErrors && (
                    <div className="text-sm text-red-600">
                      {Array.isArray(allErrors.metadata_json)
                        ? allErrors.metadata_json.join(', ')
                        : (allErrors.metadata_json as unknown as string)}
                    </div>
                  )}
                  {allErrors.metadata && (
                    <div className="text-sm text-red-600">{allErrors.metadata.join(', ')}</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-center pt-6">
              <Button type="submit" disabled={processing} size="lg" className="px-8">
                {processing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </div>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEdit ? 'Update Divisor' : 'Create Divisor'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
